import { FeatureFlag } from '@caresend/types';
import { defineComponent, ref, computed } from 'vue';
import __vue_component__$1 from '../../cards/BasicCard/BasicCard.vue.js';
import __vue_component__$3 from './ScanningSupplyRow.vue.js';
import __vue_component__ from '../../readers/BarcodeReader/BarcodeReader.vue.js';
import __vue_component__$2 from '../../ui/Loading/Loading.vue.js';
import '@caresend/utils';
import '@sentry/vue';
import { toastError } from '../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import { getSupplyNameByID } from '../../../functions/supplies/get.js';
import { validateScannedItem } from '../../../functions/supplies/scannedItemValidation.js';
import { barcodeValidation, handleScanningError, getUpdatedItemsToScan } from '../../../functions/supplies/scanning.js';
import { trackScanItem } from '../../../functions/tracking/tracking.js';
import { getStore } from '../../../store/helpers/initStore.js';

var script = defineComponent({
    name: 'MultiSupplyFromListScanner',
    components: {
        BarcodeReader: __vue_component__,
        BasicCard: __vue_component__$1,
        Loading: __vue_component__$2,
        ScanningSupplyRow: __vue_component__$3,
    },
    props: {
        /**
         * If present, restricts scanning to items of a specific type, such as
         * supplyItem or batchItem.
         */
        expectedBarcodeType: {
            type: String,
            default: undefined,
        },
        /**
         * If present, restricts scanning to supply items matching the given IDs.
         */
        expectedSupplyItemIDs: {
            type: Array,
            default: undefined,
        },
        expectedSupplyItemStatus: {
            type: String,
            default: undefined,
        },
        /**
         * If present, restricts scanning to supply items matching the given *supply* IDs.
         */
        expectedSupplyID: {
            type: String,
            default: undefined,
        },
        /**
         * Use click mode even if supply scanning is not disabled via feature flag
         */
        forceClickMode: Boolean,
        isLoading: Boolean,
        inlineManualEntry: Boolean,
        /**
         * The array of objects with the data we need to manage the scanning of
         * each supply item
         */
        itemsToScan: {
            type: Array,
            required: true,
        },
        /** If passed, this will be shown instead of the items to scan */
        label: {
            type: String,
            default: null,
        },
        /** Show serial if available in ItemToScan.serial */
        showSerials: Boolean,
        /**
         * If present, rejects scan if a supply item has already been added to the
         * procedure bag for any procedure at this waypoint.
         */
        waypointIdForProcedureBagCheck: {
            type: String,
            default: undefined,
        },
        title: {
            type: String,
            default: null,
        },
        subtitle: {
            type: String,
            default: null,
        },
    },
    setup(props, { emit }) {
        const store = getStore();
        const isLoading = ref(false);
        /**
         * Internally, this component is loading during barcode and supply
         * validation. This component should also be passed prop `isLoading: true`
         * during data updates resulting from scanning, or initial data loading.
         */
        const computedIsLoading = computed(() => isLoading.value || props.isLoading);
        const disablePatientVisitSupplyScanning = computed(() => store.getters['variables/getFeatureFlagByName'](FeatureFlag.DISABLE_PATIENT_VISIT_SUPPLY_SCANNING));
        const clickMode = computed(() => props.forceClickMode || disablePatientVisitSupplyScanning.value);
        const itemsContainerTag = computed(() => clickMode.value ? 'div' : __vue_component__$1);
        const emitNewItemsToScan = (newItemsToScan) => {
            emit('updateItemsToScan', newItemsToScan);
        };
        const mostRecentScan = ref('No items scanned');
        const onDecode = async ({ resolve, barcode }) => {
            isLoading.value = true;
            const scannedItem = await barcodeValidation(barcode, props.itemsToScan);
            if (!scannedItem) {
                // `barcodeValidation` will submit a tracking event and display an
                // appropriate toast.
                isLoading.value = false;
                return resolve(false);
            }
            if (!props.itemsToScan) {
                isLoading.value = false;
                handleScanningError('Error with Items to scan. Please contact CareSend.', scannedItem.barcode);
                return resolve(false);
            }
            // Get the updated items to scan object - returns null if the item
            // is not in the list/is already scanned.
            const newItemsToScan = getUpdatedItemsToScan(props.itemsToScan, scannedItem);
            // `updateItemsToScan` will submit a tracking event and display an
            // appropriate toast.
            if (!newItemsToScan) {
                isLoading.value = false;
                return resolve(false);
            }
            const { expectedBarcodeType, expectedSupplyID, expectedSupplyItemIDs, expectedSupplyItemStatus, waypointIdForProcedureBagCheck, } = props;
            const validateScannedItemParams = {
                scannedItem,
                expectedBarcodeType,
                expectedSupplyID,
                expectedSupplyItemIDs,
                expectedSupplyItemStatus,
                waypointIdForProcedureBagCheck,
            };
            const isValid = await validateScannedItem(validateScannedItemParams);
            if (!isValid) {
                // `validateScannedItem` will submit a tracking event and display
                // an appropriate toast.
                isLoading.value = false;
                return resolve(false);
            }
            emitNewItemsToScan(newItemsToScan);
            /** Update most recent scan label */
            const supplyName = getSupplyNameByID(scannedItem.supplyID);
            if (supplyName)
                mostRecentScan.value = `Last scanned: ${supplyName}`;
            // Only track if the scan was successful. Tracking of invalid scans
            // is separately handled with handleScanningError or handleScanningWarning.
            trackScanItem(scannedItem.barcode);
            isLoading.value = false;
            resolve(true);
        };
        /** Only triggered if scanning is disabled */
        const clickOnSupplyItem = (itemData) => {
            var _a;
            if ((_a = props.itemsToScan.find((item) => item.key === itemData.key)) === null || _a === void 0 ? void 0 : _a.isScanned) {
                toastError('Supply already selected');
                return;
            }
            /** Emit the new items to scan with the mock scanned supply item */
            isLoading.value = true;
            // Get the updated items to scan object - returns null if the item
            // is not in the list/is already scanned.
            const newItemsToScan = getUpdatedItemsToScan(props.itemsToScan, undefined, itemData.key);
            // `updateItemsToScan` will submit a tracking event and display an
            // appropriate toast.
            if (!newItemsToScan) {
                isLoading.value = false;
                return;
            }
            emitNewItemsToScan(newItemsToScan);
            isLoading.value = false;
        };
        return {
            clickMode,
            clickOnSupplyItem,
            computedIsLoading,
            disablePatientVisitSupplyScanning,
            itemsContainerTag,
            mostRecentScan,
            onDecode,
        };
    },
});

export { script as default };
